import React, { useState } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';
import { resetPassword, confirmResetPassword } from '@aws-amplify/auth';
import CustomModal from './CustomModal';
import styles from './ResetPassword.module.css';

interface ResetPasswordProps {
  show: boolean;
  onHide: () => void;
  email: string;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ show, onHide, email }) => {
  const [step, setStep] = useState<'requestReset' | 'resetPassword' | 'success'>('requestReset');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    confirmationCode: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const resetState = () => {
    setStep('requestReset');
    setLoading(false);
    setError(null);
    setFormData({
      confirmationCode: '',
      newPassword: '',
      confirmNewPassword: ''
    });
  };

  const validateResetForm = () => {
    if (!formData.confirmationCode || !formData.newPassword || !formData.confirmNewPassword) {
      setError('All fields are required');
      return false;
    }
    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.newPassword !== formData.confirmNewPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    setLoading(true);
    try {
      await resetPassword({
        username: email
      });
      setStep('resetPassword');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while requesting password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateResetForm()) return;

    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: formData.confirmationCode,
        newPassword: formData.newPassword
      });
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during password reset');
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (step) {
      case 'requestReset': return 'Reset Password';
      case 'resetPassword': return 'Enter Reset Code';
      case 'success': return 'Success!';
      default: return 'Reset Password';
    }
  };

  return (
    <CustomModal show={show} onHide={onHide} title={getModalTitle()}>
      {step === 'requestReset' ? (
        <Form onSubmit={handleRequestReset}>
          <p>We'll send a reset code to:</p>
          <div className={styles.emailHighlight}>{email}</div>
          {error && <div className={styles.error}>{error}</div>}
          <Button variant="primary" type="submit" disabled={loading} className="w-100">
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <i className="bi bi-envelope-fill me-2"></i>
                Send Reset Code
              </>
            )}
          </Button>
        </Form>
      ) : step === 'resetPassword' ? (
        <Form onSubmit={handleResetPassword}>
          <p>We've sent a reset code to:</p>
          <div className={styles.emailHighlight}>{email}</div>
          <Form.Group className="mb-3 mt-3">
            <Form.Label>Reset Code</Form.Label>
            <Form.Control
              type="text"
              value={formData.confirmationCode}
              onChange={(e) => setFormData({ ...formData, confirmationCode: e.target.value })}
              placeholder="Enter reset code"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>New Password</Form.Label>
            <Form.Control
              type="password"
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              placeholder="Enter new password"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Confirm New Password</Form.Label>
            <Form.Control
              type="password"
              value={formData.confirmNewPassword}
              onChange={(e) => setFormData({ ...formData, confirmNewPassword: e.target.value })}
              placeholder="Confirm new password"
            />
          </Form.Group>
          {error && <div className={styles.error}>{error}</div>}
          <Button variant="primary" type="submit" disabled={loading} className="w-100">
            {loading ? <Spinner animation="border" size="sm" /> : 'Reset Password'}
          </Button>
        </Form>
      ) : (
        <div className={styles.successContainer}>
          <i className={`bi bi-check-circle-fill ${styles.successIcon}`}></i>
          <h4 className={styles.successTitle}>Password Reset Successful!</h4>
          <p className={styles.successMessage}>
            Your password has been reset successfully.
          </p>
          <Button variant="primary" onClick={onHide} className="w-100">
            Continue
          </Button>
        </div>
      )}
    </CustomModal>
  );
};

export default ResetPassword; 